import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import {
  Package, Plus, Pencil, Archive, Loader2, Save, Boxes, Tag, Users,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * V13.14 Phase C — Admin Bundle Manager.
 * Route: /admin/bundles
 *
 * Lists all bundles, allows create + edit (member courses/batches),
 * and surfaces active-holders count + explicit broadcast toggle when composition
 * changes.
 */
export default function BundleManager() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // bundle obj
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [bRes, cRes, batRes] = await Promise.all([
        api.get('/api/admin/bundles'),
        api.get('/api/courses'),
        api.get('/api/batches').catch(() => ({ data: [] })),
      ]);
      setBundles(bRes.data.bundles || []);
      setCourses(cRes.data.courses || cRes.data || []);
      setBatches(batRes.data.batches || batRes.data || []);
    } catch {
      toast.error('Failed to load bundles');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="space-y-4 max-w-4xl">
      <Skeleton className="h-10 w-60" />
      <Skeleton className="h-64 rounded-2xl" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="bundle-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Bundles</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Package courses + batches into one purchase. Price is owned by OPS (set via SKU).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowArchived((v) => !v)}
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            data-testid="toggle-archived-btn"
          >
            {showArchived ? 'Hide archived' : 'Show archived'}
          </button>
          <Button onClick={() => setCreating(true)} data-testid="create-bundle-btn">
            <Plus className="h-4 w-4 mr-1" />New bundle
          </Button>
        </div>
      </div>

      {bundles.length === 0 ? (
        <Card className="p-10 text-center">
          <Package className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            No bundles yet. Bundles let you mint a single ticket that covers multiple courses + batches.
          </p>
          <Button onClick={() => setCreating(true)} className="mt-4" variant="outline">
            <Plus className="h-4 w-4 mr-1" />Create your first bundle
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {bundles
            .filter((b) => showArchived ? true : b.is_active !== false)
            .map((b) => (
            <Card key={b.id} className={`p-4 ${!b.is_active ? 'opacity-60' : ''}`} data-testid={`bundle-row-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Boxes className="h-4 w-4 text-rose-600" />
                    <h3 className="font-medium">{b.title}</h3>
                    <Badge variant="outline" className="text-[10px]">v{b.version}</Badge>
                    {b.ops_sku && (
                      <Badge variant="outline" className="text-[10px]">
                        <Tag className="h-3 w-3 mr-1" />{b.ops_sku}
                      </Badge>
                    )}
                    {!b.is_active && <Badge className="text-[10px] bg-slate-200 text-slate-700">Archived</Badge>}
                  </div>
                  {b.description && <p className="text-xs text-muted-foreground mt-1">{b.description}</p>}
                  <div className="flex gap-3 mt-2 text-[11px] text-muted-foreground">
                    <span>{(b.member_course_ids || []).length} courses</span>
                    <span>·</span>
                    <span>{(b.member_batch_ids || []).length} batches</span>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => setEditing(b)} data-testid={`edit-bundle-${b.id}`}>
                  <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {creating && (
        <BundleEditor
          open
          onClose={() => setCreating(false)}
          courses={courses}
          batches={batches}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {editing && (
        <BundleEditor
          open
          bundle={editing}
          onClose={() => setEditing(null)}
          courses={courses}
          batches={batches}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function BundleEditor({ open, onClose, bundle, courses, batches, onSaved }) {
  const isEdit = !!bundle;
  const [form, setForm] = useState({
    key: bundle?.key || '',
    title: bundle?.title || '',
    description: bundle?.description || '',
    ops_sku: bundle?.ops_sku || '',
    currency: bundle?.currency || 'INR',
    member_course_ids: bundle?.member_course_ids || [],
    member_batch_ids: bundle?.member_batch_ids || [],
    is_active: bundle?.is_active !== false,
  });
  const [applyToHolders, setApplyToHolders] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [hydration, setHydration] = useState(null);

  useEffect(() => {
    if (bundle) {
      (async () => {
        try {
          const r = await api.get(`/api/admin/bundles/${bundle.id}`);
          setHydration(r.data);
        } catch { /* noop */ }
      })();
    }
  }, [bundle]);

  const toggleCourse = (cid) => {
    setForm((p) => ({
      ...p,
      member_course_ids: p.member_course_ids.includes(cid)
        ? p.member_course_ids.filter((x) => x !== cid)
        : [...p.member_course_ids, cid],
    }));
  };
  const toggleBatch = (bid) => {
    setForm((p) => ({
      ...p,
      member_batch_ids: p.member_batch_ids.includes(bid)
        ? p.member_batch_ids.filter((x) => x !== bid)
        : [...p.member_batch_ids, bid],
    }));
  };

  const save = async () => {
    if (!form.title || !form.key) { toast.error('Title + key required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const r = await api.patch(`/api/admin/bundles/${bundle.id}`, {
          ...form,
          apply_to_existing_holders: applyToHolders,
        });
        const composedChanged = r.data?.composition_changed;
        const holders = r.data?.broadcast?.holders_updated;
        toast.success(
          composedChanged && applyToHolders
            ? `Bundle saved → propagated to ${holders || 0} active holders`
            : 'Bundle saved',
        );
      } else {
        await api.post('/api/admin/bundles', form);
        toast.success('Bundle created');
      }
      onSaved();
    } catch {
      toast.error('Save failed');
    }
    setSaving(false);
  };

  const deactivate = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Archive "${bundle.title}"? Existing tickets keep working; no new tickets will be minted.`)) return;
    setDeactivating(true);
    try {
      await api.delete(`/api/admin/bundles/${bundle.id}`);
      toast.success('Bundle archived');
      onSaved();
    } catch { toast.error('Failed'); }
    setDeactivating(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bundle-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit: ${bundle.title}` : 'New bundle'}</DialogTitle>
        </DialogHeader>
        {isEdit && hydration?.active_holders_count != null && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-900 flex items-center gap-2">
            <Users className="h-3.5 w-3.5" />
            <span><strong>{hydration.active_holders_count}</strong> active holders. Changing composition won't affect them unless you opt-in below.</span>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium mb-1 block">Key (internal)</label>
            <Input value={form.key} disabled={isEdit} onChange={(e) => setForm((p) => ({ ...p, key: e.target.value }))}
              placeholder="garbhsanskar_prenatal_bundle" data-testid="bundle-key-input" />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">OPS SKU</label>
            <Input value={form.ops_sku} onChange={(e) => setForm((p) => ({ ...p, ops_sku: e.target.value }))}
              placeholder="bundle_prenatal_v1" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium mb-1 block">Title</label>
            <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Pre-natal essentials bundle" data-testid="bundle-title-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium mb-1 block">Description</label>
            <Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={2} placeholder="One shot. Every course + batch for your journey." />
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium mb-2">Courses included ({form.member_course_ids.length})</p>
            <div className="border rounded-lg max-h-44 overflow-auto divide-y">
              {courses.length === 0 && <p className="p-3 text-xs text-muted-foreground">No courses.</p>}
              {courses.map((c) => {
                const id = c._id || c.id;
                const checked = form.member_course_ids.includes(id);
                return (
                  <label key={id} className={`flex items-center gap-2 p-2 cursor-pointer text-sm ${checked ? 'bg-rose-50' : 'hover:bg-muted/30'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleCourse(id)}
                      data-testid={`bundle-course-${id}`} />
                    <span>{c.title}</span>
                    {c.ops_sku && <Badge variant="outline" className="text-[9px] ml-auto">{c.ops_sku}</Badge>}
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium mb-2">Batches included ({form.member_batch_ids.length})</p>
            <div className="border rounded-lg max-h-36 overflow-auto divide-y">
              {batches.length === 0 && <p className="p-3 text-xs text-muted-foreground">No batches.</p>}
              {batches.map((b) => {
                const id = b._id || b.id;
                const checked = form.member_batch_ids.includes(id);
                return (
                  <label key={id} className={`flex items-center gap-2 p-2 cursor-pointer text-sm ${checked ? 'bg-rose-50' : 'hover:bg-muted/30'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleBatch(id)}
                      data-testid={`bundle-batch-${id}`} />
                    <span>{b.name || b.batch_code || id}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {isEdit && (
          <div className="bg-slate-50 border rounded-lg p-3 text-xs flex items-start gap-3">
            <Switch
              checked={applyToHolders}
              onCheckedChange={setApplyToHolders}
              id="apply-holders"
              data-testid="apply-to-holders-switch"
            />
            <div>
              <label htmlFor="apply-holders" className="font-medium">
                Apply to existing holders
              </label>
              <p className="text-muted-foreground text-[11px] mt-0.5">
                When ON, active bundle tickets have their snapshots rewritten to match
                the new composition. Bumps the bundle version. Use sparingly.
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between gap-2">
          {isEdit && (
            <Button variant="ghost" onClick={deactivate} disabled={deactivating}
              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 mr-auto" data-testid="archive-bundle-btn">
              {deactivating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Archive className="h-3.5 w-3.5 mr-1" />}
              Archive
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-bundle-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {isEdit ? 'Save' : 'Create bundle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
